import React, { useEffect, useState } from 'react';
import { Location, useLocation, RouterProvider, createBrowserRouter, useNavigate, Outlet } from "react-router";
import { Helmet } from 'react-helmet-async';
import { GlobalStyle } from 'styles/global-styles';

import { MasterTemplate } from "./components/MasterTemplate";
import { useContext } from "react";
import { AppContext } from "./context/AppContext";

import { initMatomo } from '../utils/Matomo/matomo';
import { MatomoTracker } from "./components/Matomo/MatomoTracker";
import { GlobalClickTracker } from "./components/Matomo/GlobalClickTracker";
import ConsentManager from "./components/ConstentManager/ConsentManager";

/**
 * Layout
 */
export function Layout(){
  const {
    I18N
  } = useContext(AppContext);

  /**
   * Check if we’re running in production mode.
   * If true, we will load Matomo + Consent Manager.
   * If false (development), we skip them completely.
   */
  const isProd = process.env.REACT_APP_RUN_ENV === "production";

  /**
   * this is used to detect async location in react router due to a unfixed bug overall versions
   */
  const navigate = useNavigate(),
    _location = useLocation();

  /**
   * we inject window.location.pathname into router location
   * to force it to be right after navigate('myroute') call
   */
  const [location, setLocation] = useState<Location>({
    ..._location,
    pathname: window.location.pathname
  });

  /**
   * listen to changes on window.location.pathname and set
   * location to correct pathname value after all call navigate
   * to render correct route to pathname
   */
  useEffect(() => {
      setLocation({
        ..._location,
        pathname: window.location.pathname
      });

      // no viewTransition here: this replace targets the URL we are already
      // on, so there is nothing to animate -- but requesting one starts a
      // second transition right after every Link click, which interrupts the
      // Link's still-running animation (skipTransition) and surfaces as an
      // unhandled AbortError/InvalidStateError rejection on every navigation
      navigate(window.location.href.replace(window.location.origin, ""), {
        replace: true
      });
  // eslint-disable-next-line
  }, [window.location.pathname]);

  /**
   * the page scrolls inside dia-grid, not the window, so neither the browser
   * nor react-router resets the offset on a route change: coming from a
   * scrolled page the next route rendered mid-scroll with the breadcrumb cut
   * off
   */
  useEffect(() => {
    document.querySelector("dia-grid")?.scrollTo(0, 0);
  }, [location.pathname]);

  /**
   * Only call initMatomo() if we’re in production.
   * In development, this effect does nothing.
   */
  useEffect(() => {
    if (isProd) {
      initMatomo();
    }
  }, [isProd]);

  /**
   * return base layout with helmet, master template and global styles
   */
  return <>
    {/* Mount ConsentManager and Matomo only when isProd === true */}
    {isProd && <ConsentManager />}
    {isProd && <MatomoTracker />}
    {isProd && <GlobalClickTracker />}
    <Helmet
      titleTemplate="%s - DIA Interlink"
      defaultTitle={ I18N.t("DIA Interlink") }
      htmlAttributes={{ lang: I18N.getLocale() }}
    >
      <meta name="description" content="" />
    </Helmet>
    <MasterTemplate location={ location }>
      <Outlet />
    </MasterTemplate>
    <GlobalStyle />
  </>
}

/**
 * App
 * @constructor
 */
export function App() {
  const {
    I18NisReady,
    getRouteChildren
  } = useContext(AppContext);

  /**
   * create browser router with layout as base object
   * additionally we add all children from the object list above
   */
  return (
    I18NisReady ? <RouterProvider
      router={ createBrowserRouter(
        [
          {
            element: <Layout />,
            children: getRouteChildren()
          }
        ]
      ) }
    /> : <></>
  );
}
